/**
 * How the checkup's licence list is divided on screen.
 *
 * This lives here, not in Checkup.tsx, because the bug it guards against LIVED in the render.
 * The first draft grouped rows by `journey_group` and told the developer "choose one route —
 * you need ONE of them, not all N", over two mandatory, prerequisite-linked TCN filings. There
 * is no component-test setup in this repo, so nothing could see it: the regression was
 * reintroduced during review and 440 tests stayed green. A rule that decides whether a
 * developer is shown a licence they legally need does not get to live somewhere untestable.
 *
 * A pure function of the evaluation output. No React, no DB.
 */
import type { SurfaceClass } from "./applicability.ts";

export interface CheckupLicenseRow {
  license_type_id: string;
  license_name: string;
  authority: string;
  readiness_pct: number;
  applicability?: string;
  applicability_reason?: string | null;
  undetermined?: boolean;
  surface?: SurfaceClass;
  elective?: boolean;
  lifecycle_stage?: string | null;
  journey_group?: string | null;
  subject?: string;
}

export interface CheckupPartition {
  /** Day-one obligations, required on the answers given. */
  required: CheckupLicenseRow[];
  /** Day-one, but hanging on a fact we did not ask for — a question FOR THE DEVELOPER. */
  conditional: CheckupLicenseRow[];
  /** Day-one, but the corpus records no basis — a gap on OUR side. */
  underReview: CheckupLicenseRow[];
  /** Opportunities. Nobody is in breach for declining one. */
  elective: CheckupLicenseRow[];
  /** Renewals and amendments — they act on a licence you do not hold yet. */
  lifecycle: CheckupLicenseRow[];
  /** Binds your contractor or your equipment, not you. */
  thirdParty: CheckupLicenseRow[];
}

/**
 * Every row lands in EXACTLY ONE bucket. Nothing is grouped away, merged, or deduplicated:
 *
 *  - `journey_group` is NOT exclusivity. The corpus defines it as a presentational grouping
 *    ("rows sharing a group render as one workplan card") and the generator auto-backfills
 *    `spine:<phase>` onto rows that lack one, purely to hit a coverage target. Two rows in a
 *    group can be sequential and BOTH mandatory — TCN Grid Code Accession is a documented
 *    prerequisite of the Transmission Use of System Agreement. Collapsing them into "pick one"
 *    tells a developer to skip a filing they legally need.
 *
 *  - An unknown `surface` from a future corpus falls back to `baseline`: shown as an
 *    obligation. Over-showing is recoverable; hiding a required licence is not.
 */
export function partitionCheckupLicenses(rows: CheckupLicenseRow[]): CheckupPartition {
  const p: CheckupPartition = {
    required: [], conditional: [], underReview: [], elective: [], lifecycle: [], thirdParty: [],
  };

  for (const l of rows) {
    switch (l.surface) {
      case "elective":    p.elective.push(l); continue;
      case "lifecycle":   p.lifecycle.push(l); continue;
      case "third_party": p.thirdParty.push(l); continue;
      // "baseline", undefined (a legacy result predating `surface`), or a value this build
      // does not recognise — all fall through to the day-one tiers, which is the safe side.
    }
    if (l.undetermined) p.underReview.push(l);
    else if (l.applicability === "applicable") p.required.push(l);
    else p.conditional.push(l);
  }

  return p;
}
